import { useState, useEffect } from 'react';

/**
 * Custom hook for typing animation effect
 * @param {string[]} words - Array of words to cycle through
 * @param {number} typingSpeed - Speed of typing in ms (default: 100)
 * @param {number} deletingSpeed - Speed of deleting in ms (default: 50)
 * @param {number} pauseTime - Time to pause at complete word in ms (default: 2000)
 * @returns {string} The current typed text
 */
const useTypingAnimation = (
  words = ['Simple', 'Affordable'],
  typingSpeed = 100,
  deletingSpeed = 50,
  pauseTime = 2000,
  enabled = true,
  staticWord = words[0] || ''
) => {
  const staticWordIndex = Math.max(words.indexOf(staticWord), 0);
  const [typedText, setTypedText] = useState(() => enabled ? '' : staticWord);
  const [wordIndex, setWordIndex] = useState(() => enabled ? 0 : staticWordIndex);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDocumentVisible, setIsDocumentVisible] = useState(() => typeof document === 'undefined' || !document.hidden);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const update = () => setIsDocumentVisible(!document.hidden);
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);

  useEffect(() => {
    if (!enabled) {
      if (typedText !== staticWord) setTypedText(staticWord);
      if (wordIndex !== staticWordIndex) setWordIndex(staticWordIndex);
      if (isDeleting) setIsDeleting(false);
      return undefined;
    }
    if (!isDocumentVisible) return undefined;

    const currentWord = words[wordIndex];
    let pauseTimer;

    const timer = setTimeout(() => {
      if (!isDeleting && typedText === currentWord) {
        // Word complete, pause then start deleting
        pauseTimer = setTimeout(() => setIsDeleting(true), pauseTime);
      } else if (isDeleting && typedText === '') {
        // Done deleting, move to next word
        setIsDeleting(false);
        setWordIndex((prev) => (prev + 1) % words.length);
      } else {
        // Still typing or deleting
        setTypedText(
          isDeleting
            ? currentWord.substring(0, typedText.length - 1)
            : currentWord.substring(0, typedText.length + 1)
        );
      }
    }, isDeleting ? deletingSpeed : typingSpeed);

    return () => {
      clearTimeout(timer);
      if (pauseTimer) clearTimeout(pauseTimer);
    };
  }, [typedText, isDeleting, wordIndex, words, typingSpeed, deletingSpeed, pauseTime, enabled, staticWord, staticWordIndex, isDocumentVisible]);

  return typedText;
};

export default useTypingAnimation;
